import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Eye, EyeOff } from "lucide-react";
import Logo from "@/components/Logo";

const bullets = ["7-day free trial", "Cancel anytime", "No card required"];

export default function Signup() {
  const { signup, loginWithGoogle } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const nav = useNavigate();
  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be 6+ characters"); return; }
    setBusy(true);
    try {
      await signup(form.email, form.password, form.name);
      toast.success("Welcome to BidForge");
      nav("/profile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bf-light theme-light min-h-screen flex bg-white" data-testid="signup-page">
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(160deg, #EEF2FF 0%, #FDF2F8 50%, #FFF7ED 100%)" }} />
        <div aria-hidden className="absolute top-40 -right-20 w-[500px] h-[500px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.30), transparent 70%)" }} />
        <div aria-hidden className="absolute -bottom-20 -left-20 w-[500px] h-[500px] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(249,115,22,0.20), transparent 70%)" }} />
        <div className="relative p-16 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2" data-testid="signup-home-link">
            <Logo className="w-9 h-9" />
            <div className="font-display font-black text-lg tracking-tight text-slate-900">BIDFORGE</div>
          </Link>
          <div>
            <div className="bf-eyebrow inline-block text-orange-600 mb-6">DEPLOY YOUR BD DEPARTMENT</div>
            <h1 className="font-display font-black text-5xl leading-[0.98] tracking-tight text-slate-900 mb-4">One profile.<br/><span className="bf-grad-text-warm">Unlimited opportunities.</span></h1>
            <p className="text-slate-600 max-w-md text-lg">Your AI will start scanning federal contracts within seconds of onboarding.</p>
            <div className="mt-8 flex flex-col gap-2">
              {bullets.map((b) => (
                <span key={b} className="inline-flex items-center gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-emerald-500" />{b}</span>
              ))}
            </div>
          </div>
          <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400">7-DAY TRIAL · NO CARD REQUIRED</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10" data-testid="signup-home-link-mobile">
            <Logo className="w-9 h-9" />
            <div className="font-display font-black text-lg text-slate-900 tracking-tight">BIDFORGE</div>
          </Link>
          <div className="bf-eyebrow inline-block text-violet-600 mb-4">CREATE ACCOUNT</div>
          <h2 className="font-display font-black text-3xl md:text-4xl text-slate-900 mb-8 tracking-tight">Start winning today</h2>

          <button
            onClick={loginWithGoogle}
            className="w-full rounded-full border border-slate-200 py-3 text-slate-800 hover:border-violet-400 hover:text-violet-700 hover:bg-violet-50/50 transition-all mb-6 flex items-center justify-center gap-3 font-medium"
            data-testid="google-signup-button"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC04" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6"><div className="flex-1 h-px bg-slate-200" /><span className="text-[10px] font-mono font-bold tracking-widest text-slate-400">OR</span><div className="flex-1 h-px bg-slate-200" /></div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-2">YOUR NAME</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} required className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-4 py-3 text-slate-900 outline-none transition-all" data-testid="signup-name" />
            </div>
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-2">WORK EMAIL</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 px-4 py-3 text-slate-900 outline-none transition-all" data-testid="signup-email" />
            </div>
            <div>
              <label className="text-[10px] font-mono font-bold tracking-widest text-slate-500 block mb-2">PASSWORD</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="w-full rounded-xl bg-white border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-100 pl-4 pr-12 py-3 text-slate-900 outline-none transition-all" style={{WebkitTextFillColor: '#0F172A'}} data-testid="signup-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-violet-600 p-1" data-testid="signup-password-toggle" tabIndex={-1}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={busy} className="bf-pill w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50" data-testid="signup-submit">
              {busy ? "…" : <>CREATE ACCOUNT <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>

          <div className="mt-8 text-center text-sm text-slate-500">
            Already deployed?{" "}
            <Link to="/login" className="text-violet-600 hover:text-violet-700 font-semibold transition-colors" data-testid="link-login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
